import React from 'react';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { usePartnerContent } from '@/hooks/usePartnerContent';
import { PageLoading } from '@/features/static/shared';
import { PartnerHero } from './components/PartnerHero';
import { PartnerFeatureCard } from './components/PartnerFeatureCard';
import { PartnerSteps } from './components/PartnerSteps';
import { PartnerCtaBand } from './components/PartnerCtaBand';
import { PARTNER_COLORS } from './constants';
import { TrendingUp, Sparkles, Link2, CheckCircle2 } from 'lucide-react';

const WHY_ICONS = [TrendingUp, Sparkles, Link2];

export const AnslutByggvaruhusPage = () => {
  const { settings, loading, mailto } = usePartnerContent();
  const page = settings?.anslut || {};

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: PARTNER_COLORS.bg }}>
        <PageLoading />
      </div>
    );
  }

  const whyCards = (page.why_cards || []).slice(0, 3);
  const requirements = page.requirements || [];
  const steps = (page.onboarding_steps || []).map((title) => ({ title }));

  return (
    <div className="min-h-screen" style={{ backgroundColor: PARTNER_COLORS.bg }} data-testid="anslut-byggvaruhus-page">
      <SeoMeta
        title="Anslut ditt byggvaruhus"
        description={page.hero_subtitle}
        canonicalPath="/bli-partner/anslut-byggvaruhus"
      />

      <PartnerHero
        title={page.hero_title}
        subtitle={page.hero_subtitle}
        heroImageUrl={page.hero_image_url}
        primaryLabel={page.hero_button_label}
        primaryHref={mailto}
        primaryMailto
      />

      <div className="container-main py-4">
        <Breadcrumbs
          items={[
            { label: 'Bli partner', href: '/bli-partner' },
            { label: 'Anslut byggvaruhus' },
          ]}
        />
      </div>

      <section className="border-t border-[#E5E7EB] bg-white py-16 md:py-20">
        <div className="container-main max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-12" style={{ color: PARTNER_COLORS.navy }}>
            {page.why_title}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {whyCards.map((card, i) => (
              <PartnerFeatureCard
                key={card.title || i}
                icon={WHY_ICONS[i] || TrendingUp}
                title={card.title}
                description={card.description}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="container-main max-w-3xl mx-auto px-4 py-16 md:py-20 border-t border-[#E5E7EB]">
        <h2 className="text-2xl md:text-3xl font-black mb-8" style={{ color: PARTNER_COLORS.navy }}>
          {page.requirements_title}
        </h2>
        <ul className="space-y-4">
          {requirements.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: PARTNER_COLORS.accent }} />
              <span className="text-[16px] leading-relaxed" style={{ color: PARTNER_COLORS.textMuted }}>
                {item}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-[#E5E7EB] bg-white py-16 md:py-20">
        <div className="container-main max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-12" style={{ color: PARTNER_COLORS.navy }}>
            {page.onboarding_title}
          </h2>
          <PartnerSteps steps={steps} />
        </div>
      </section>

      <PartnerCtaBand
        title={page.final_cta_title}
        buttonLabel={page.final_cta_button_label}
        mailto={mailto}
      />
    </div>
  );
};

export default AnslutByggvaruhusPage;
